/**
 * Decides what happens to a template's background when it is unlocked
 * (re-synced to the shared background).
 *
 * A per-template upload must not linger past unlock — re-locking later would
 * resurrect it with its stale cover-fit transform. But bgX/bgY/bgScale are
 * also how the user pans the shared/gallery/baked background per template, so
 * the revert must run ONLY when the template actually carries its own upload;
 * otherwise unlock silently resets user positioning.
 *
 * "Carries its own upload" is subtle on Livestream: a shared upload mirrors
 * the image into every template's customBgs slot AND sets customBgUploaded
 * for each (see BgSelector's livestream branch), so the flag alone is not
 * enough — the slot must also have diverged from the shared copy.
 */

export type UnlockBgRevert =
  | {action: "none"}
  | {action: "clear"}
  | {action: "revert-to-shared"; sharedBg: string};

export function resolveUnlockBgRevert({
  channelValue,
  customBgs,
  customBgUploaded,
  templateId,
}: {
  channelValue: string | undefined;
  customBgs: Record<string, string | undefined> | undefined;
  customBgUploaded: Record<string, boolean | undefined> | undefined;
  templateId: string;
}): UnlockBgRevert {
  // Livestream renders customBgs[id] directly (no .main fallback in its
  // templates), so it is the only channel where unlock rewrites the slot to
  // the shared copy instead of just dropping it.
  const sharedLivestreamBg =
    channelValue === "livestream" ? customBgs?.main : undefined;

  const hasOwnUpload =
    !!customBgUploaded?.[templateId] &&
    (!sharedLivestreamBg || customBgs?.[templateId] !== sharedLivestreamBg);
  if (!hasOwnUpload) return {action: "none"};

  if (sharedLivestreamBg) return {action: "revert-to-shared", sharedBg: sharedLivestreamBg};
  return {action: "clear"};
}
